import React, { useState, useEffect } from "react";
import managedAxios, { isCancelError } from "@/utils/managedAxios";
import { useAuth } from "@/auth/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { useToast } from "@/hooks/use-toast";
import { API_BASE } from "@/App";
import { 
  Brain, TrendingUp, TrendingDown, Minus, Target, AlertTriangle, 
  Users, XCircle, RefreshCw, ChevronRight, Shield, Zap,
  BarChart3, Clock, ArrowRight
} from "lucide-react";

/**
 * CLARIAS CORE INTELLIGENCE ENGINE
 * Decision Intelligence for Competitive Exams
 * 
 * 4 Mandatory Modules:
 * 1. Selection Probability Curve
 * 2. "If Exam Were Tomorrow" Simulation
 * 3. Topper Benchmark Ghost
 * 4. Mistake Repetition Warning System
 */
const IntelligenceEnginePage = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  
  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchIntelligenceReport();
  }, []);

  const fetchIntelligenceReport = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await managedAxios.get(`${API_BASE}/intelligence/report`);
      setReport(data);
    } catch (err) {
      console.error("Error fetching intelligence report:", err);
      setError("Could not load intelligence report. Please try again.");
      toast({ variant: "destructive", description: "Failed to load intelligence report" });
    } finally {
      setLoading(false);
    }
  };

  const getTrendIcon = (trend) => {
    switch (trend) {
      case "improving": return <TrendingUp className="w-4 h-4 text-green-600" />;
      case "declining": return <TrendingDown className="w-4 h-4 text-red-600" />;
      default: return <Minus className="w-4 h-4 text-amber-600" />;
    }
  };

  const getTrendColor = (trend) => {
    switch (trend) {
      case "improving": return "text-green-600";
      case "declining": return "text-red-600";
      default: return "text-amber-600";
    }
  };

  const getProbabilityColor = (prob) => {
    if (prob >= 70) return "from-green-500 to-emerald-500";
    if (prob >= 50) return "from-amber-500 to-yellow-500";
    if (prob >= 30) return "from-orange-500 to-red-500";
    return "from-red-600 to-red-800";
  };

  const getRiskBadgeColor = (risk) => {
    switch (risk) {
      case "CRITICAL": return "bg-red-600 text-white";
      case "HIGH": return "bg-red-500 text-white";
      case "MEDIUM": return "bg-amber-500 text-white";
      default: return "bg-slate-500 text-white";
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin text-indigo-500 mx-auto mb-4" />
          <p className="text-slate-400">Analyzing your performance data...</p>
        </div>
      </div>
    );
  }

  if (error || !report) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <Card className="bg-slate-800 border-slate-700 max-w-md">
          <CardContent className="p-6 text-center">
            <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
            <p className="text-slate-300 mb-4">{error || "No data available"}</p>
            <Button onClick={fetchIntelligenceReport}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Retry
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { selection_probability, exam_simulation, topper_benchmark, mistake_warnings } = report;
  const isPro = report.access_level === "PRO";

  return (
    <div className="min-h-screen bg-slate-900 text-slate-100">
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-800 to-slate-900 border-b border-slate-700">
        <div className="max-w-6xl mx-auto px-6 py-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-red-600 to-orange-500 flex items-center justify-center shadow-lg">
                <Brain className="w-8 h-8 text-white" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="text-2xl font-bold text-white">CLARIAS</h1>
                  <Badge className={isPro ? "bg-indigo-600 text-white" : "bg-slate-600 text-slate-300"}>
                    {isPro ? "PRO" : "FREE"}
                  </Badge>
                </div>
                <p className="text-slate-400">Decision Intelligence • No False Hope</p>
              </div>
            </div>
            <Button 
              variant="outline" 
              size="sm"
              onClick={fetchIntelligenceReport}
              className="border-slate-600 text-slate-300 hover:bg-slate-700"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh Analysis
            </Button>
          </div>

          {/* CLARIAS Philosophy Banner */}
          <div className="mt-6 p-4 bg-slate-800/50 border border-slate-700 rounded-lg">
            <p className="text-xs text-slate-500 uppercase tracking-wide mb-2">Core Philosophy</p>
            <p className="text-sm text-slate-300 italic">
              &ldquo;Aspirants fail due to wrong decisions, not lack of information. 
              <span className="text-red-400 font-medium"> Comfort is dangerous; clarity saves attempts.</span>&rdquo;
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8 space-y-6">

        {/* Weekly Update Card (PRO Only) */}
        {isPro && report.weekly_update && (
          <Card className="bg-gradient-to-r from-indigo-900/50 to-purple-900/50 border-indigo-700">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-white text-base">
                <Zap className="w-4 h-4 text-yellow-400" />
                Weekly Intelligence Update
                <Badge className="ml-auto bg-indigo-600 text-white text-xs">PRO</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-4 gap-4">
                <div className="text-center p-3 bg-slate-800/50 rounded-lg">
                  <p className={`text-2xl font-bold ${
                    report.weekly_update.probability_change >= 0 ? "text-green-400" : "text-red-400"
                  }`}>
                    {report.weekly_update.probability_change >= 0 ? "+" : ""}
                    {report.weekly_update.probability_change}%
                  </p>
                  <p className="text-xs text-slate-400">Probability Change</p>
                </div>
                <div className="text-center p-3 bg-slate-800/50 rounded-lg">
                  <p className="text-2xl font-bold text-white">{report.weekly_update.tests_taken}</p>
                  <p className="text-xs text-slate-400">Tests This Week</p>
                </div>
                <div className="text-center p-3 bg-slate-800/50 rounded-lg">
                  <p className={`text-2xl font-bold ${
                    report.weekly_update.accuracy_change >= 0 ? "text-green-400" : "text-red-400"
                  }`}>
                    {report.weekly_update.accuracy_change >= 0 ? "+" : ""}
                    {report.weekly_update.accuracy_change}%
                  </p>
                  <p className="text-xs text-slate-400">Accuracy Change</p>
                </div>
                <div className="text-center p-3 bg-slate-800/50 rounded-lg">
                  <p className="text-lg font-bold text-amber-400">{report.weekly_update.primary_risk_factor}</p>
                  <p className="text-xs text-slate-400">Primary Risk</p>
                </div>
              </div>
              <div className="p-3 bg-red-950/30 border border-red-900/50 rounded-lg">
                <p className="text-sm text-red-300">
                  ⚠️ {report.weekly_update.hard_truth_insight}
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Locked Features Notice (FREE users) */}
        {!isPro && report.locked_features && (
          <Card className="bg-slate-800/50 border-amber-700/50">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <Shield className="w-5 h-5 text-amber-500 mt-0.5" />
                <div className="flex-1">
                  <p className="font-medium text-amber-400">FREE Access - Limited Insights</p>
                  <p className="text-xs text-slate-400 mt-1">
                    Shows WHAT happened. Upgrade to PRO to see WHY it happened.
                  </p>
                  <div className="flex flex-wrap gap-2 mt-3">
                    {report.locked_features.slice(0, 3).map((feature, idx) => (
                      <Badge key={idx} variant="outline" className="border-slate-600 text-slate-400 text-xs">
                        🔒 {feature}
                      </Badge>
                    ))}
                  </div>
                  <Button 
                    size="sm" 
                    className="mt-3 bg-amber-600 hover:bg-amber-700 text-white"
                    onClick={() => window.location.href = "/upgrade"}
                  >
                    Unlock Full Diagnostics
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
        
        {/* ════════════════════════════════════════
            MODULE 1: SELECTION PROBABILITY CURVE
            ════════════════════════════════════════ */}
        <Card className="bg-slate-800 border-slate-700 overflow-hidden">
          <div className={`h-2 bg-gradient-to-r ${getProbabilityColor(selection_probability.probability)}`} />
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-white">
                <Target className="w-5 h-5 text-indigo-400" />
                1️⃣ Selection Probability Curve
              </div>
              <div className="flex items-center gap-2">
                {getTrendIcon(selection_probability.trend)}
                <span className={`text-sm ${getTrendColor(selection_probability.trend)}`}>
                  {selection_probability.trend.charAt(0).toUpperCase() + selection_probability.trend.slice(1)}
                </span>
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Main Probability Display */}
            <div className="flex items-center justify-center py-6">
              <div className="relative">
                <div className={`w-40 h-40 rounded-full bg-gradient-to-br ${getProbabilityColor(selection_probability.probability)} flex items-center justify-center shadow-2xl`}>
                  <div className="w-32 h-32 rounded-full bg-slate-900 flex flex-col items-center justify-center">
                    <span className="text-4xl font-bold text-white">{selection_probability.probability}%</span>
                    <span className="text-xs text-slate-400">Selection Probability</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Component Breakdown */}
            <div className="grid grid-cols-5 gap-3">
              {Object.entries(selection_probability.components).map(([key, value]) => (
                <div key={key} className="text-center p-3 bg-slate-700/50 rounded-lg">
                  <p className="text-lg font-bold text-white">{value}%</p>
                  <p className="text-[10px] text-slate-400 uppercase tracking-wide">
                    {key.replace("_", " ")}
                  </p>
                </div>
              ))}
            </div>

            {/* Hard Truth */}
            <div className="p-4 bg-red-950/30 border border-red-900/50 rounded-lg">
              <p className="text-sm text-red-300 font-medium">
                ⚠️ {selection_probability.hard_truth}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* ════════════════════════════════════════
            MODULE 2: IF EXAM WERE TOMORROW
            ════════════════════════════════════════ */}
        <Card className="bg-slate-800 border-slate-700">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-white">
              <Clock className="w-5 h-5 text-amber-400" />
              2️⃣ If Exam Were Tomorrow
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Score Projection */}
            <div className="grid md:grid-cols-3 gap-4">
              <div className="p-4 bg-slate-700/50 rounded-lg text-center">
                <p className="text-3xl font-bold text-white">
                  {exam_simulation.expected_score_min}-{exam_simulation.expected_score_max}
                </p>
                <p className="text-xs text-slate-400 mt-1">Expected Score Range</p>
              </div>
              <div className="p-4 bg-slate-700/50 rounded-lg text-center">
                <p className="text-3xl font-bold text-white">{exam_simulation.likely_cutoff}</p>
                <p className="text-xs text-slate-400 mt-1">Likely Cutoff</p>
              </div>
              <div className="p-4 bg-slate-700/50 rounded-lg text-center">
                <p className={`text-3xl font-bold ${
                  exam_simulation.cutoff_gap >= 0 ? "text-green-400" : "text-red-400"
                }`}>
                  {exam_simulation.cutoff_gap >= 0 ? "+" : ""}{exam_simulation.cutoff_gap}
                </p>
                <p className="text-xs text-slate-400 mt-1">Cutoff Gap</p>
              </div>
            </div>

            {/* Pass Probability */}
            <div className="flex items-center justify-between p-4 bg-slate-700/30 rounded-lg">
              <span className="text-slate-300">Pass Probability</span>
              <Badge className={`text-sm ${
                exam_simulation.pass_probability.includes("High") ? "bg-green-600" :
                exam_simulation.pass_probability.includes("Moderate") ? "bg-amber-600" :
                exam_simulation.pass_probability.includes("Borderline") ? "bg-orange-600" :
                "bg-red-600"
              } text-white`}>
                {exam_simulation.pass_probability}
              </Badge>
            </div>

            {/* Top Failure Reasons */}
            <div className="space-y-2">
              <p className="text-sm font-medium text-red-400">Top 3 Failure Reasons:</p>
              {exam_simulation.top_failure_reasons.map((reason, idx) => (
                <div key={idx} className="flex items-start gap-3 p-3 bg-red-950/20 border border-red-900/30 rounded-lg">
                  <XCircle className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />
                  <p className="text-sm text-red-200">{reason}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* ════════════════════════════════════════
            MODULE 3: TOPPER BENCHMARK GHOST
            ════════════════════════════════════════ */}
        <Card className="bg-slate-800 border-slate-700">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-white">
                <Users className="w-5 h-5 text-purple-400" />
                3️⃣ Topper Benchmark Ghost
              </div>
              <Badge variant="outline" className="border-purple-500 text-purple-400">
                vs Top 10%
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Overall Gap */}
            <div className="flex items-center justify-between p-4 bg-purple-950/20 border border-purple-900/30 rounded-lg">
              <div>
                <p className="text-sm text-slate-400">Overall Gap from Top 10%</p>
                <p className="text-2xl font-bold text-white">{topper_benchmark.overall_gap_percentage}%</p>
              </div>
              <div className="text-right">
                <p className="text-xs text-slate-400">Closest</p>
                <p className="text-sm text-green-400">{topper_benchmark.closest_metric}</p>
                <p className="text-xs text-slate-400 mt-2">Furthest</p>
                <p className="text-sm text-red-400">{topper_benchmark.furthest_metric}</p>
              </div>
            </div>

            {/* Gap Breakdown */}
            <div className="space-y-3">
              {topper_benchmark.gaps.map((gap, idx) => (
                <div key={idx} className="p-4 bg-slate-700/30 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-white">{gap.metric}</span>
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-slate-400">
                        You: <span className="text-white">{gap.user_value}%</span>
                      </span>
                      <span className="text-slate-600">→</span>
                      <span className="text-sm text-slate-400">
                        Top 10%: <span className="text-purple-400">{gap.benchmark_value}%</span>
                      </span>
                    </div>
                  </div>
                  <Progress 
                    value={(gap.user_value / gap.benchmark_value) * 100} 
                    className="h-2 bg-slate-700"
                  />
                  <p className="text-xs text-slate-400 mt-2">{gap.description}</p>
                </div>
              ))}
            </div>

            {/* Message */}
            <div className="p-4 bg-slate-700/50 rounded-lg">
              <p className="text-sm text-slate-300">
                <span className="text-purple-400 font-medium">Analysis: </span>
                {topper_benchmark.message}
              </p>
            </div>

            {/* PRO-only behavioral recommendations */}
            {isPro && topper_benchmark.behavioral_recommendations && (
              <div className="p-4 bg-indigo-950/30 border border-indigo-800/50 rounded-lg">
                <p className="text-xs text-indigo-400 font-medium mb-2 flex items-center gap-2">
                  <Zap className="w-3 h-3" />
                  PRO Behavioral Recommendations
                </p>
                <ul className="space-y-2">
                  {topper_benchmark.behavioral_recommendations.map((rec, idx) => (
                    <li key={idx} className="text-sm text-slate-300 flex items-start gap-2">
                      <ChevronRight className="w-4 h-4 text-indigo-400 mt-0.5" />
                      {rec}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </CardContent>
        </Card>

        {/* ════════════════════════════════════════
            MODULE 4: MISTAKE REPETITION WARNING
            ════════════════════════════════════════ */}
        <Card className="bg-slate-800 border-slate-700">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-white">
                <AlertTriangle className="w-5 h-5 text-red-400" />
                4️⃣ Mistake Repetition Warning System
              </div>
              {mistake_warnings.length > 0 && (
                <Badge className="bg-red-600 text-white">
                  {mistake_warnings.length} Active Warning{mistake_warnings.length > 1 ? "s" : ""}
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {mistake_warnings.length === 0 ? (
              <div className="p-6 text-center">
                <Shield className="w-12 h-12 text-green-500 mx-auto mb-3" />
                <p className="text-slate-300">No repeated cognitive errors detected yet.</p>
                <p className="text-xs text-slate-500 mt-2">
                  Errors will be tracked as you take more tests.
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {mistake_warnings.map((warning, idx) => (
                  <div 
                    key={idx} 
                    className={`p-4 rounded-lg border ${
                      warning.risk_level === "CRITICAL" 
                        ? "bg-red-950/40 border-red-800" 
                        : warning.risk_level === "HIGH"
                        ? "bg-red-950/30 border-red-900/50"
                        : "bg-amber-950/30 border-amber-900/50"
                    }`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <AlertTriangle className={`w-5 h-5 ${
                          warning.risk_level === "CRITICAL" ? "text-red-500" : 
                          warning.risk_level === "HIGH" ? "text-red-400" : "text-amber-500"
                        }`} />
                        <span className="font-medium text-white">{warning.error_name}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={getRiskBadgeColor(warning.risk_level)}>
                          {warning.risk_level}
                        </Badge>
                        <Badge variant="outline" className="border-slate-600 text-slate-300">
                          {warning.occurrences}x
                        </Badge>
                      </div>
                    </div>
                    <p className={`text-sm ${
                      warning.risk_level === "CRITICAL" ? "text-red-200" : "text-slate-300"
                    }`}>
                      {warning.warning_message}
                    </p>
                    {/* PRO-only correction steps */}
                    {isPro && warning.correction_steps && warning.correction_steps.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-slate-700">
                        <p className="text-xs text-indigo-400 font-medium mb-2">Correction Steps (PRO):</p>
                        <ul className="space-y-1">
                          {warning.correction_steps.map((step, stepIdx) => (
                            <li key={stepIdx} className="text-xs text-slate-400 flex items-start gap-2">
                              <ChevronRight className="w-3 h-3 mt-0.5 text-indigo-400" />
                              {step}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Closing Message */}
        <div className="text-center py-8">
          <p className="text-lg text-slate-400 italic">
            &ldquo;{report.closing_message}&rdquo;
          </p>
        </div>

        {/* Action CTA */}
        <Card className="bg-gradient-to-r from-indigo-900 to-purple-900 border-0">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white font-medium">Ready to improve?</p>
                <p className="text-indigo-200 text-sm">Start addressing your weak points now.</p>
              </div>
              <Button 
                className="bg-white text-indigo-700 hover:bg-slate-100"
                onClick={() => window.location.href = "/clarity"}
              >
                Go to Clarity Assistant
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default IntelligenceEnginePage;
